import { Link } from "react-router-dom";
import { categories } from "@/mocks/content";

export interface ArticleCardData {
  id: string;
  slug: string;
  title: string;
  summary: string;
  category: string;
  cover: string;
  author: string;
  publishedAt: string;
  views: number;
  tags: string[];
}

export const categoryName = (slug: string) =>
  categories.find((item) => item.slug === slug)?.name ?? "瓜料";

export const formatViews = (views: number) =>
  views >= 10000 ? `${(views / 10000).toFixed(1)}万` : `${views}`;

interface Props {
  article: ArticleCardData;
  featured?: boolean;
}

export default function ArticleCard({ article, featured = false }: Props) {
  if (featured) {
    return (
      <article className="group overflow-hidden rounded-lg border border-background-200/80 bg-background-100/60 transition-colors duration-200 hover:border-primary-300">
        <Link to={`/news/${article.id}`} className="block">
          <div className="relative h-52 w-full overflow-hidden sm:h-64">
            <img
              src={article.cover}
              alt={`${article.title} 封面图`}
              title={`${article.title} - 吃瓜日报`}
              className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
            />
            <span className="absolute left-3 top-3 rounded-full bg-background-50/95 px-2.5 py-1 text-xs font-medium text-primary-600">
              {categoryName(article.category)}
            </span>
          </div>
        </Link>
        <div className="p-5">
          <Link to={`/news/${article.id}`}>
            <h3 className="font-heading text-lg font-bold leading-snug text-foreground-950 transition-colors duration-200 group-hover:text-primary-600">
              {article.title}
            </h3>
          </Link>
          <p className="clamp-2 mt-2.5 text-sm leading-relaxed text-foreground-600">
            {article.summary}
          </p>
          <div className="mt-4 flex items-center gap-3 text-xs text-foreground-500">
            <span>{article.author}</span>
            <span className="h-1 w-1 rounded-full bg-background-300" />
            <span>{article.publishedAt}</span>
            <span className="ml-auto flex items-center gap-1">
              <i className="ri-eye-line text-sm" aria-hidden="true" />
              {formatViews(article.views)}
            </span>
          </div>
        </div>
      </article>
    );
  }

  return (
    <article className="group flex gap-4 rounded-lg border border-background-200/70 bg-background-50 p-3.5 transition-colors duration-200 hover:border-primary-300 hover:bg-background-100/60">
      <Link to={`/news/${article.id}`} className="shrink-0">
        <div className="h-20 w-28 overflow-hidden rounded-md sm:h-24 sm:w-36">
          <img
            src={article.cover}
            alt={`${article.title} 配图`}
            title={`${article.title} - 吃瓜日报`}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
        </div>
      </Link>
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex items-center gap-2 text-xs">
          <span className="rounded bg-secondary-100 px-2 py-0.5 font-medium text-secondary-900">
            {categoryName(article.category)}
          </span>
          <span className="text-foreground-500">{article.publishedAt.slice(5, 10)}</span>
        </div>
        <Link to={`/news/${article.id}`}>
          <h3 className="clamp-2 mt-1.5 font-heading text-base font-semibold leading-snug text-foreground-950 transition-colors duration-200 group-hover:text-primary-600">
            {article.title}
          </h3>
        </Link>
        <p className="clamp-2 mt-1.5 hidden text-sm leading-relaxed text-foreground-600 sm:block">
          {article.summary}
        </p>
        <div className="mt-auto flex items-center gap-3 pt-2 text-xs text-foreground-500">
          <span>{article.author}</span>
          <span className="ml-auto flex items-center gap-1">
            <i className="ri-eye-line text-sm" aria-hidden="true" />
            {formatViews(article.views)}
          </span>
        </div>
      </div>
    </article>
  );
}